import {
  ComponentStyle,
  DeviceBreakpoint,
  DeviceStyleOverrides,
  TestViewportWidth,
} from '../types';

export const BREAKPOINTS = {
  mobile: { min: 0, max: 767, defaultWidth: 390 },
  tablet: { min: 768, max: 1023, defaultWidth: 768 },
  desktop: { min: 1024, max: Infinity, defaultWidth: 1280 },
} as const;

export interface ViewportPreset {
  id: string;
  label: string;
  width: TestViewportWidth;
  device: DeviceBreakpoint;
  iconType: 'mobile' | 'tablet' | 'desktop';
  description: string;
  isDefault?: boolean;
}

export const PRESET_VIEWPORTS: ViewportPreset[] = [
  {
    id: 'mobile-320',
    label: 'Kompakt (320px)',
    width: 320,
    device: 'mobile',
    iconType: 'mobile',
    description: 'Küçük ekranlı telefonlar (iPhone SE 1, eski Android)',
  },
  {
    id: 'mobile-360',
    label: 'Android Standart (360px)',
    width: 360,
    device: 'mobile',
    iconType: 'mobile',
    description: 'Çoğu popüler Samsung / Xiaomi telefon',
  },
  {
    id: 'mobile-375',
    label: 'iPhone SE/8 (375px)',
    width: 375,
    device: 'mobile',
    iconType: 'mobile',
    description: 'iPhone SE, iPhone 12/13 mini',
  },
  {
    id: 'mobile-390',
    label: 'iPhone 14/15/16 (390px)',
    width: 390,
    device: 'mobile',
    iconType: 'mobile',
    description: 'En popüler modern iPhone ekran genişliği',
    isDefault: true,
  },
  {
    id: 'mobile-414',
    label: 'Büyük Telefon / Plus (414px)',
    width: 414,
    device: 'mobile',
    iconType: 'mobile',
    description: 'iPhone Plus / Pro Max ve geniş Android modelleri',
  },
  {
    id: 'tablet-768',
    label: 'iPad / Tablet (768px)',
    width: 768,
    device: 'tablet',
    iconType: 'tablet',
    description: 'Dikey iPad ve standart 10" tabletler',
    isDefault: true,
  },
  {
    id: 'tablet-1024',
    label: 'Tablet Yatay / Laptop (1024px)',
    width: 1024,
    device: 'tablet',
    iconType: 'tablet',
    description: 'iPad Pro yatay veya küçük ekranlı dizüstü bilgisayarlar',
  },
  {
    id: 'desktop-1280',
    label: 'Masaüstü Standart (1280px)',
    width: 1280,
    device: 'desktop',
    iconType: 'desktop',
    description: 'Standart masaüstü ve 13-14" dizüstü bilgisayarlar',
    isDefault: true,
  },
  {
    id: 'desktop-1440',
    label: 'Geniş Ekran / 2K (1440px)',
    width: 1440,
    device: 'desktop',
    iconType: 'desktop',
    description: 'Geniş monitörler ve 15-16" MacBook Pro ekranları',
  },
  {
    id: 'desktop-1920',
    label: 'Tam Ekran / FHD (1920px)',
    width: 1920,
    device: 'desktop',
    iconType: 'desktop',
    description: '1080p Full HD masaüstü ve harici monitörler',
  },
];

/**
 * Resolves a responsive cascade property with precedence:
 * Mobile Override -> Tablet Override -> Desktop Base Value -> Default Fallback
 */
export function resolveResponsiveProperty<T>(
  device: DeviceBreakpoint,
  baseValue: T | undefined,
  mobileValue: T | undefined,
  tabletValue: T | undefined,
  defaultValue: T
): T {
  if (device === 'mobile') {
    if (mobileValue !== undefined && mobileValue !== null) return mobileValue;
    if (tabletValue !== undefined && tabletValue !== null) return tabletValue;
    if (baseValue !== undefined && baseValue !== null) return baseValue;
    return defaultValue;
  }

  if (device === 'tablet') {
    if (tabletValue !== undefined && tabletValue !== null) return tabletValue;
    if (baseValue !== undefined && baseValue !== null) return baseValue;
    return defaultValue;
  }

  // Desktop
  if (baseValue !== undefined && baseValue !== null) return baseValue;
  return defaultValue;
}

/**
 * Returns a fully-resolved representation of ComponentStyle for a specific device viewport
 */
export function resolveStyleForDevice(
  style: ComponentStyle | undefined,
  device: DeviceBreakpoint = 'desktop'
): Required<Pick<ComponentStyle, 'columns' | 'alignment' | 'sectionHeight' | 'containerWidth' | 'buttonWidth'>> & {
  fontSize?: string;
  headingFontSize?: string;
  isHidden: boolean;
  mobileStack: boolean;
  reverseOrder: boolean;
  aspectRatio: string;
  objectFit: 'cover' | 'contain';
} {
  const base = style || {};
  const mobile = base.mobileOverrides || {};
  const tablet = base.tabletOverrides || {};

  // Check visibility flags
  let isHidden = false;
  if (device === 'mobile') {
    isHidden = !!(base.hideOnMobile || base.responsive?.hideOnMobile || mobile.hide);
  } else if (device === 'tablet') {
    isHidden = !!(base.hideOnTablet || base.responsive?.hideOnTablet || tablet.hide);
  } else {
    isHidden = !!(base.hideOnDesktop || base.responsive?.hideOnDesktop);
  }

  // Automatic default responsive columns: Desktop base (e.g. 3 or 4) -> Tablet (2) -> Mobile (1)
  const defaultBaseCols = base.columns || 3;
  const defaultTabletCols = Math.min(defaultBaseCols, 2);
  const defaultMobileCols = 1;

  const columns = resolveResponsiveProperty<number>(
    device,
    base.columns,
    mobile.columns,
    tablet.columns,
    device === 'mobile' ? defaultMobileCols : device === 'tablet' ? defaultTabletCols : defaultBaseCols
  );

  const alignment = resolveResponsiveProperty<'left' | 'center' | 'right'>(
    device,
    base.alignment,
    mobile.alignment,
    tablet.alignment,
    'center'
  );

  const sectionHeight = resolveResponsiveProperty<'compact' | 'normal' | 'spacious' | 'fullscreen'>(
    device,
    base.sectionHeight,
    mobile.sectionHeight,
    tablet.sectionHeight,
    'normal'
  );

  const containerWidth = resolveResponsiveProperty<'contained' | 'full' | 'compact' | 'standard' | 'wide'>(
    device,
    base.containerWidth,
    mobile.containerWidth,
    tablet.containerWidth,
    'wide'
  );

  const buttonWidth = resolveResponsiveProperty<'auto' | 'full'>(
    device,
    base.buttonWidth,
    mobile.buttonWidth,
    tablet.buttonWidth,
    device === 'mobile' ? 'full' : 'auto'
  );

  const fontSize = resolveResponsiveProperty<string | undefined>(
    device,
    base.fontSize,
    mobile.fontSize,
    tablet.fontSize,
    undefined
  );

  const headingFontSize = resolveResponsiveProperty<string | undefined>(
    device,
    base.headingFontSize,
    mobile.headingFontSize,
    tablet.headingFontSize,
    undefined
  );

  const mobileStack = resolveResponsiveProperty<boolean>(
    device,
    base.mobileStack ?? true,
    mobile.mobileStack,
    tablet.mobileStack,
    true
  );

  const reverseOrder = resolveResponsiveProperty<boolean>(
    device,
    base.reverseOrder ?? false,
    mobile.reverseOrder,
    tablet.reverseOrder,
    false
  );

  const aspectRatio = resolveResponsiveProperty<string>(
    device,
    base.aspectRatio || 'auto',
    mobile.aspectRatio,
    tablet.aspectRatio,
    'auto'
  );

  const objectFit = resolveResponsiveProperty<'cover' | 'contain'>(
    device,
    base.objectFit || 'cover',
    mobile.objectFit,
    tablet.objectFit,
    'cover'
  );

  return {
    columns,
    alignment,
    sectionHeight,
    containerWidth,
    buttonWidth,
    fontSize,
    headingFontSize,
    isHidden,
    mobileStack,
    reverseOrder,
    aspectRatio,
    objectFit,
  };
}

/**
 * Checks if a particular style property is customized on the active device
 */
export function isFieldOverridden(
  style: ComponentStyle | undefined,
  device: DeviceBreakpoint,
  field: keyof DeviceStyleOverrides
): boolean {
  if (!style) return false;
  if (device === 'mobile') {
    return style.mobileOverrides?.[field] !== undefined;
  }
  if (device === 'tablet') {
    return style.tabletOverrides?.[field] !== undefined;
  }
  return false;
}

/**
 * Returns a human-friendly string for the inheritance status of a property
 */
export function getInheritanceSourceLabel(
  style: ComponentStyle | undefined,
  device: DeviceBreakpoint,
  field: keyof DeviceStyleOverrides
): string {
  if (device === 'desktop') return 'Masaüstü (Ana Değer)';

  if (device === 'mobile') {
    if (style?.mobileOverrides?.[field] !== undefined) {
      return 'Özel Mobil Değer';
    }
    if (style?.tabletOverrides?.[field] !== undefined) {
      return 'Tabletten Devralındı';
    }
    return 'Masaüstünden Devralındı';
  }

  if (device === 'tablet') {
    if (style?.tabletOverrides?.[field] !== undefined) {
      return 'Özel Tablet Değer';
    }
    return 'Masaüstünden Devralındı';
  }

  return 'Varsayılan';
}

/**
 * Updates a style object safely, directing changes to device overrides or base values
 */
export function applyStyleChange(
  currentStyle: ComponentStyle | undefined,
  device: DeviceBreakpoint,
  updates: Partial<ComponentStyle>
): ComponentStyle {
  const existing = currentStyle ? { ...currentStyle } : {};

  if (device === 'desktop') {
    // When editing in Desktop mode, update base properties
    return {
      ...existing,
      ...updates,
    };
  }

  if (device === 'mobile') {
    // When in Mobile mode, write into mobileOverrides without overwriting desktop values
    const currentMobile = existing.mobileOverrides || {};
    const updatedMobile: DeviceStyleOverrides = { ...currentMobile };

    // Extract device-specific overrides from updates
    const overrideKeys: Array<keyof DeviceStyleOverrides> = [
      'fontSize',
      'headingFontSize',
      'textAlign',
      'alignment',
      'paddingTop',
      'paddingBottom',
      'paddingLeft',
      'paddingRight',
      'sectionHeight',
      'columns',
      'containerWidth',
      'buttonWidth',
      'mobileStack',
      'reverseOrder',
      'aspectRatio',
      'objectFit',
      'focalPoint',
      'hide',
    ];

    overrideKeys.forEach((key) => {
      if ((updates as any)[key] !== undefined) {
        (updatedMobile as any)[key] = (updates as any)[key];
      }
    });

    // Also copy any direct mobileOverrides if passed
    if (updates.mobileOverrides) {
      Object.assign(updatedMobile, updates.mobileOverrides);
    }

    return {
      ...existing,
      mobileOverrides: updatedMobile,
      // preserve any non-device base styles (like colors, border, etc.)
      backgroundColor: updates.backgroundColor !== undefined ? updates.backgroundColor : existing.backgroundColor,
      textColor: updates.textColor !== undefined ? updates.textColor : existing.textColor,
      presetStyle: updates.presetStyle !== undefined ? updates.presetStyle : existing.presetStyle,
      borderRadius: updates.borderRadius !== undefined ? updates.borderRadius : existing.borderRadius,
      borderColor: updates.borderColor !== undefined ? updates.borderColor : existing.borderColor,
      shadow: updates.shadow !== undefined ? updates.shadow : existing.shadow,
    };
  }

  if (device === 'tablet') {
    const currentTablet = existing.tabletOverrides || {};
    const updatedTablet: DeviceStyleOverrides = { ...currentTablet };

    const overrideKeys: Array<keyof DeviceStyleOverrides> = [
      'fontSize',
      'headingFontSize',
      'textAlign',
      'alignment',
      'paddingTop',
      'paddingBottom',
      'paddingLeft',
      'paddingRight',
      'sectionHeight',
      'columns',
      'containerWidth',
      'buttonWidth',
      'mobileStack',
      'reverseOrder',
      'aspectRatio',
      'objectFit',
      'focalPoint',
      'hide',
    ];

    overrideKeys.forEach((key) => {
      if ((updates as any)[key] !== undefined) {
        (updatedTablet as any)[key] = (updates as any)[key];
      }
    });

    if (updates.tabletOverrides) {
      Object.assign(updatedTablet, updates.tabletOverrides);
    }

    return {
      ...existing,
      tabletOverrides: updatedTablet,
    };
  }

  return { ...existing, ...updates };
}

/**
 * Resets a single property override for a given device back to inherited value
 */
export function resetFieldOverride(
  currentStyle: ComponentStyle | undefined,
  device: DeviceBreakpoint,
  field: keyof DeviceStyleOverrides
): ComponentStyle {
  if (!currentStyle) return {};
  const updated = { ...currentStyle };

  if (device === 'mobile' && updated.mobileOverrides) {
    const nextMobile = { ...updated.mobileOverrides };
    delete nextMobile[field];
    updated.mobileOverrides = Object.keys(nextMobile).length > 0 ? nextMobile : undefined;
  } else if (device === 'tablet' && updated.tabletOverrides) {
    const nextTablet = { ...updated.tabletOverrides };
    delete nextTablet[field];
    updated.tabletOverrides = Object.keys(nextTablet).length > 0 ? nextTablet : undefined;
  }

  return updated;
}

/**
 * Resets all overrides for a given device
 */
export function resetAllDeviceOverrides(
  currentStyle: ComponentStyle | undefined,
  device: DeviceBreakpoint
): ComponentStyle {
  if (!currentStyle) return {};
  const updated = { ...currentStyle };

  if (device === 'mobile') {
    delete updated.mobileOverrides;
  } else if (device === 'tablet') {
    delete updated.tabletOverrides;
  }

  return updated;
}

// ----------------------------------------------------
// CSS Helper Functions for Component Layouts
// ----------------------------------------------------

/**
 * Returns responsive Tailwind grid class for cards based on resolved column count
 */
export function getGridColumnsClass(columns: number = 3): string {
  switch (columns) {
    case 1:
      return 'grid grid-cols-1 gap-6';
    case 2:
      return 'grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8';
    case 4:
      return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8';
    case 3:
    default:
      return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8';
  }
}

/**
 * Returns container max-width class
 */
export function getContainerWidthClass(containerWidth: string = 'wide'): string {
  switch (containerWidth) {
    case 'compact':
      return 'max-w-4xl mx-auto px-4 sm:px-6';
    case 'standard':
      return 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8';
    case 'full':
      return 'w-full px-4 sm:px-6 lg:px-12';
    case 'wide':
    default:
      return 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';
  }
}

/**
 * Returns vertical padding class
 */
export function getSectionHeightPaddingClass(sectionHeight: string = 'normal'): string {
  switch (sectionHeight) {
    case 'compact':
      return 'py-10 sm:py-14';
    case 'spacious':
      return 'py-20 sm:py-28 lg:py-36';
    case 'fullscreen':
      return 'min-h-[80vh] sm:min-h-[90vh] lg:min-h-screen py-16 sm:py-24 flex items-center';
    case 'normal':
    default:
      return 'py-16 sm:py-20 lg:py-24';
  }
}
